import { Component, Host, h, Element, Prop, Watch, Listen } from '@stencil/core';
import { TabsType } from './tabs-interface';

/**
 * @slot - The slot for tab content elements.
 * @slot navigation - The slot for tab elements.
 *
 * @part navigation - The component's tab list navigation wrapper.
 * @part content - The component's content wrapper.
 */
@Component({
  tag: 'o2-tabs',
  styleUrl: 'tabs.scss',
  shadow: true,
})
export class O2Tabs {
  @Element() el!: HTMLO2TabsElement;

  /**
   * Types of tabs.
   * Possible values: `"default"`, `"underline"`,
   */
  @Prop() type?: TabsType = 'default';

  /**
   * Defines actually selected tab by its panel name.
   */
  @Prop({ reflect: true, mutable: true }) selected?: string;

  @Watch('selected')
  watchSelectedHandler({}, oldVal: string) {
    if (oldVal) {
      this.unsetCurrentTab();
      this.unsetCurrentTabContent();
    }

    this.setSelectedTab();
    this.setSelectedTabContent();
  }

  @Listen('o2Selected')
  protected selectedTabHandler(event) {
    this.selected = event.detail?.selected;
  }

  /**
   * Listens to keyboard arrows and enter key to navigate through the list.
   */
  @Listen('keydown')
  protected handleKeyDown(event: KeyboardEvent) {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;

    event.preventDefault();

    const tabs = Array.from(this.el.querySelectorAll<HTMLO2TabElement>('o2-tab'));
    const selectedIndex = tabs.findIndex(tab => tab.hasAttribute('selected'));
    const lastIndex = tabs.length - 1;

    let newIndex: number;

    if (event.key === 'ArrowRight') {
      newIndex = selectedIndex === -1 || selectedIndex === lastIndex ? 0 : selectedIndex + 1;
    } else {
      newIndex = selectedIndex === -1 || selectedIndex === 0 ? lastIndex : selectedIndex - 1;
    }

    tabs.forEach(tab => tab.removeAttribute('selected'));
    tabs[newIndex]?.setAttribute('selected', '');
  }

  private setDefaultSelected() {
    const firstTab = this.el.querySelector('o2-tab');
    this.selected = firstTab.panel;
    firstTab.selected = true;
    const panelForFirstTab = this.el.querySelector('o2-tab-content[name=' + firstTab.panel + ']');
    panelForFirstTab.classList.add('selected');
  }

  private unsetCurrentTab() {
    const tab: HTMLO2TabElement = this.el.querySelector(`o2-tab[selected]`);
    tab.selected = false;
  }

  private setSelectedTab() {
    const tab: HTMLO2TabElement = this.el.querySelector(`o2-tab[panel=${this.selected}]`);
    tab.selected = true;
  }

  private setSelectedTabContent() {
    const tabContent: HTMLO2TabContentElement = this.el.querySelector(`o2-tab-content[name=${this.selected}]`);
    tabContent.selected = true;
  }

  private unsetCurrentTabContent() {
    const tabContent: HTMLO2TabContentElement = this.el.querySelector(`o2-tab-content[selected]`);
    tabContent.selected = false;
  }

  connectedCallback() {
    const swiper = this.el.querySelector('o2-swiper');
    const tabs = this.el.querySelectorAll('o2-tab');
    let slotNavigationCounter = 0;

    // Check if slot=navigation exists - legacy workaround
    for (let index = 0; index < this.el.children.length; index++) {
      const child = this.el.children[index];

      if (child.getAttribute('slot') && child.getAttribute('slot') === 'navigation') {
        slotNavigationCounter++;
      }
    }

    if (swiper) {
      tabs.forEach((tab: HTMLO2TabElement) => {
        tab.classList.add('o2-swiper__slide');
      });
    }

    if (slotNavigationCounter === 0) {
      if (swiper) {
        // add slot to swiper if not set outside - legacy workaround
        swiper.setAttribute('slot', 'navigation');
      } else {
        // add slot to each tab if not set outside - legacy workaround
        tabs.forEach((tab: HTMLO2TabElement) => {
          if (tab.slot !== 'navigation') {
            tab.setAttribute('slot', 'navigation');
          }
        });
      }
    }
  }

  componentWillLoad() {
    if (this.selected) {
      this.setSelectedTab();
      this.setSelectedTabContent();
    } else {
      this.setDefaultSelected();
    }
  }

  componentDidLoad() {
    const swiper = this.el.querySelector('o2-swiper');
    if (this.type === 'underline') {
      swiper && swiper.querySelector('.o2-swiper__wrapper').classList.add('justify-content-md-start');
    } else {
      // when using swiper to navigate tabs, add class to justify content center on desktop
      swiper && swiper.querySelector('.o2-swiper__wrapper').classList.add('justify-content-md-center');
    }
  }

  render() {
    return (
      <Host role="tablist">
        <div class={`o2-tabs type--${this.type}`}>
          <div class="o2-tabs__navigation" role="tablist" part="navigation">
            <slot name="navigation"></slot>
          </div>
          <div class="o2-tabs__content" part="content">
            <slot></slot>
          </div>
        </div>
      </Host>
    );
  }
}
